import { useEffect, useRef } from 'react'

/**
 * Escape-to-close.
 *
 * Every overlay in the Quasar app added its own `document.addEventListener
 * ('keyup', ...)` inside `created()` and only two of them ever removed it, so
 * closing one overlay could fire the handler of another that had been unmounted
 * minutes earlier. Here the listener is torn down with the component, and
 * `enabled` lets only the topmost overlay respond.
 */
export function useEscapeKey(onEscape: () => void, enabled = true): void {
  const handler = useRef(onEscape)
  handler.current = onEscape

  useEffect(() => {
    if (!enabled) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        handler.current()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [enabled])
}
